"use client";

import type { AIModel } from "@saas/ai-image-generator/types";
import type {
	AiModelListItem,
	ModelBrand,
} from "@saas/shared/config/ai-models";
import { Badge } from "@ui/components/badge";
import { cn } from "@ui/lib";
import { LockIcon } from "lucide-react";
import { Fragment, type ReactNode } from "react";
import { ModelBrandIcon } from "./ModelBrandIcon";
import { ModelPickerItem, ModelPickerLabel } from "./ModelPicker";

// v3.0（站内默认）单独放最上面、不加标题；其余按品牌分组，组内保持清单顺序（老公 10/19 定）
const BRAND_GROUPS: { brand: ModelBrand; title?: string }[] = [
	{ brand: "imagesart" },
	{ brand: "seedream", title: "Seedream" },
	{ brand: "nano-banana", title: "Nano Banana" },
	{ brand: "gpt", title: "GPT" },
	{ brand: "qwen", title: "Qwen" },
];

/**
 * 模型下拉的内容（放在 DropdownMenuContent 或 ModelPicker 里）。工作台 AiImageGeneratorClient 和
 * /ai-image-generator 的 ImageGeneratorHero 共用，触发按钮各自写。
 */
export function ModelMenuItems({
	models,
	value,
	onSelect,
	getCredits,
	creditIcon,
	isDisabled,
	isLocked,
	lockIcon,
}: {
	models: AiModelListItem[];
	value: AIModel;
	/** 付费墙要盖在菜单上时，调用方 event.preventDefault() 让菜单别关 */
	onSelect: (model: AIModel, event: Event) => void;
	/** 切到这个模型后 Create 上会扣多少积分 */
	getCredits: (model: AIModel) => number;
	/** 跟各自 Create 按钮上的积分图标一致 */
	creditIcon: ReactNode;
	/** 点不了（游客只能用几个模型） */
	isDisabled?: (model: AIModel) => boolean;
	/** 付费专属：照样能点，点了由 onSelect 弹付费墙 */
	isLocked?: (model: AIModel) => boolean;
	lockIcon?: ReactNode;
}) {
	return BRAND_GROUPS.map(({ brand, title }) => {
		const items = models.filter((model) => model.brand === brand);
		if (items.length === 0) {
			return null;
		}
		return (
			<Fragment key={brand}>
				{title && (
					<ModelPickerLabel className="px-2.5 pt-2.5 pb-1 font-semibold text-[11px] text-muted-foreground uppercase tracking-wide">
						{title}
					</ModelPickerLabel>
				)}
				{items.map((model) => {
					const disabled = isDisabled?.(model.id) ?? false;
					const locked = isLocked?.(model.id) ?? false;
					return (
						<ModelPickerItem
							key={model.id}
							onSelect={(event) => onSelect(model.id, event)}
							className={cn(
								"flex min-w-0 cursor-pointer items-center gap-2.5 rounded-lg border border-transparent px-2.5 py-2 data-[highlighted]:bg-accent data-[highlighted]:text-accent-foreground",
								value === model.id &&
									"border-border bg-muted/30",
								disabled &&
									"pointer-events-none cursor-not-allowed opacity-50",
								locked && "opacity-60",
							)}
						>
							<ModelBrandIcon brand={model.brand} />
							<div className="min-w-0 flex-1">
								<div className="flex items-center gap-1.5">
									<p className="truncate font-medium text-foreground text-sm">
										{model.name}
									</p>
									{model.badge && (
										<Badge
											variant="secondary"
											className="h-4 flex-shrink-0 border-blue-100 bg-blue-50 px-1.5 text-[10px] text-blue-600"
										>
											{model.badge}
										</Badge>
									)}
									{locked &&
										(lockIcon ?? (
											<LockIcon className="size-3.5 flex-shrink-0 text-muted-foreground" />
										))}
								</div>
								<div className="mt-1 flex flex-wrap gap-1">
									{model.tags.map((tag) => (
										<span
											key={tag}
											className="rounded bg-muted px-1.5 text-[10.5px] text-muted-foreground leading-4"
										>
											{tag}
										</span>
									))}
								</div>
							</div>
							<span className="flex flex-shrink-0 items-center gap-1 text-foreground/80 text-xs tabular-nums">
								{creditIcon}
								{getCredits(model.id)}
							</span>
						</ModelPickerItem>
					);
				})}
			</Fragment>
		);
	});
}
